import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react';

import config from '../../../../config';
import {GiftCardEntryFormData} from '../../../../models/schemas/giftCardsSchemaZod';
import {PAYMENT_SERVICE_TYPES, usePaymentServices} from '../../../../queries/paymentCard';
import {useStores} from '../../../../stores/use-stores';
import {centsToDollarsString} from '../../../../utils/functions';
import {ZenGiftCardsPaymentInstrumentWithChargedAmount} from '../../../../utils/giftCards';
import {logger} from '../../../../utils/logger';
import {Icon} from '../../../Icon';
import {DollarSignIcon, TrashIconButton} from '../../../icons';
import Button from '../../../themed/Button';
import Text from '../../../themed/Text';
import ActivityIndicator from '../../../ui-controls/ActivityIndicator';
import TextInput from '../../../ui-controls/form/TextInput';
import {ZenGiftCardEntryModal} from '../ZenGiftCardEntryModal';

type ZenGiftCardsListProps = {
  zenGiftCardPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[];
  onDeleteGiftCard: (id: string) => void;
};

const ZenGiftCardsList = ({
  zenGiftCardPaymentInstruments,
  onDeleteGiftCard,
}: ZenGiftCardsListProps) => (
  <>
    {!!zenGiftCardPaymentInstruments.length && (
      <View style={cardListStyles.main}>
        {zenGiftCardPaymentInstruments.map(zenGiftCardPaymentInstrument => (
          <ZenGiftCard
            zenGiftCardPaymentInstrument={zenGiftCardPaymentInstrument}
            key={zenGiftCardPaymentInstrument.id}
            onDelete={() => onDeleteGiftCard(zenGiftCardPaymentInstrument.id)}
          />
        ))}
      </View>
    )}
  </>
);

type ZenGiftCardProps = {
  zenGiftCardPaymentInstrument: ZenGiftCardsPaymentInstrumentWithChargedAmount;
  onDelete: () => void;
};

const ZenGiftCard = ({zenGiftCardPaymentInstrument, onDelete}: ZenGiftCardProps) => {
  const {zen_card_number: number, chargedAmount = 0, balance = 0} = zenGiftCardPaymentInstrument;

  const maskedNumber = `*${number.slice(-4)}`;
  const formattedBalance = centsToDollarsString(balance);
  const formattedAmount = centsToDollarsString(chargedAmount);

  return (
    <View
      style={{
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginBottom: 16,
      }}>
      <TextInput
        containerStyle={{width: 90}}
        disabled
        inputStyle={{width: 45}}
        label="Number"
        labelStyle={{fontSize: 14}}
        renderErrorMessage={false}
        value={maskedNumber}
      />
      <TextInput
        containerStyle={{width: 90}}
        disabled
        inputStyle={{width: 45}}
        label="Balance"
        labelStyle={{fontSize: 14}}
        leftIcon={<DollarSignIcon disabled />}
        renderErrorMessage={false}
        value={formattedBalance}
      />
      <TextInput
        containerStyle={{width: 100}}
        disabled
        inputStyle={{width: 50}}
        label="Amount"
        labelStyle={{fontSize: 14}}
        leftIcon={<DollarSignIcon disabled />}
        renderErrorMessage={false}
        value={formattedAmount}
      />
      <TrashIconButton onPress={onDelete} />
    </View>
  );
};

const ZenGiftCardsManager = observer(() => {
  const {cartStore} = useStores();
  const {data: paymentServicesData, isLoading: isLoadingPaymentServices} = usePaymentServices();
  const styles = useGiftCardStyles();

  const zenGiftCardsPaymentService = useMemo(
    () =>
      paymentServicesData?.services?.find(s => s.type === PAYMENT_SERVICE_TYPES.ZEN_GIFT_CARDS) ??
      null,
    [paymentServicesData?.services],
  );

  const [isGiftCardEntryModalVisible, setIsGiftCardEntryModalVisible] = useState(false);
  const [isAddCardSubmitting, setIsAddCardSubmitting] = useState(false);
  const [cardAddingError, setCardAddingError] = useState<string>('');

  if (isLoadingPaymentServices) {
    return <ActivityIndicator />;
  }

  if (!zenGiftCardsPaymentService) {
    logger.debug(
      'ZenGiftCardsManager',
      'No Zen Gift Cards Payment Service available in this Store',
    );
    return null;
  }

  const zenGiftCardsWithAmount = cartStore.zenGiftCardsUserPaymentInstruments;
  const hasGiftCards = zenGiftCardsWithAmount.length > 0;

  const handleGiftCardEntryModalCancel = () => {
    setIsGiftCardEntryModalVisible(false);
    setCardAddingError('');
  };

  const handleAddCardPress = () => setIsGiftCardEntryModalVisible(true);
  const handleAddCard = async (formValues: GiftCardEntryFormData) => {
    try {
      setIsAddCardSubmitting(true);
      await cartStore.addZenGiftCardsUserPaymentInstrument(
        formValues.number,
        formValues.pin,
        zenGiftCardsPaymentService!.id,
      );
      setIsGiftCardEntryModalVisible(false);
      setCardAddingError('');
    } catch (error: unknown) {
      setCardAddingError(error instanceof Error ? error.message : 'Failed to add card');
    }
    setIsAddCardSubmitting(false);
  };

  const handleDeleteGiftCard = (id: string) =>
    cartStore.deleteZenGiftCardsUserPaymentInstrument(id);

  return (
    <View>
      <Button
        containerStyle={{marginVertical: 0}}
        buttonStyle={{
          justifyContent: 'space-between',
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: 'transparent',
        }}
        type="clear"
        onPress={handleAddCardPress}>
        <Text style={styles.giftCardText}>
          {hasGiftCards
            ? 'ADD ANOTHER GIFT CARD'
            : `USE A ${config.APP_NAME.toUpperCase()} GIFT CARD`}
        </Text>
        <Icon
          name={hasGiftCards ? 'pen' : 'plus'}
          color={styles.iconStyle.color}
          style={{marginLeft: 8}}
        />
      </Button>
      <ZenGiftCardsList
        zenGiftCardPaymentInstruments={zenGiftCardsWithAmount}
        onDeleteGiftCard={handleDeleteGiftCard}
      />
      <ZenGiftCardEntryModal
        isSubmitting={isAddCardSubmitting}
        isVisible={isGiftCardEntryModalVisible}
        onAdd={handleAddCard}
        onCancel={handleGiftCardEntryModalCancel}
        error={cardAddingError}
      />
    </View>
  );
});

const useGiftCardStyles = makeStyles(theme => ({
  giftCardText: {
    color: theme.colors?.primary,
    fontWeight: '600',
    fontSize: 16,
    textAlign: 'left',
  },
  iconStyle: {
    color: theme.colors?.primary,
    marginLeft: 8,
  },
}));

export default ZenGiftCardsManager;

const cardListStyles = StyleSheet.create({
  main: {
    padding: 8,
    borderColor: 'grey',
    borderWidth: 0.4,
    borderRadius: 4,
  },
});
